import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { config } from '../../utils/config';

export const stripePublishableKey = (
  config.STRIPE_PUBLISHABLE_KEY || ''
).trim();
export const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

export type StripeCheckoutSession = {
  order_id: string;
  payment_intent_id?: string | null;
  payment_client_secret?: string | null;
  approval_required?: boolean;
};

export type StripePaymentConfirmed = {
  order_status_id: string;
  order_status_name: string;
  stripe_payment_fee?: number;
};

type StorefrontStripePaymentFormProps = {
  checkoutSession: StripeCheckoutSession;
  confirmPayment: (args: {
    order_id: string;
    payment_intent_id: string;
  }) => Promise<StripePaymentConfirmed>;
  onError: (msg: string | null) => void;
  onSuccess: (confirmed: StripePaymentConfirmed) => void;
  submitLabel?: string;
};

const StorefrontStripePaymentFormInner: React.FC<
  StorefrontStripePaymentFormProps
> = ({
  checkoutSession,
  confirmPayment,
  onError,
  onSuccess,
  submitLabel = 'Pay now',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePay = async (event: React.FormEvent) => {
    event.preventDefault();
    onError(null);
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        onError(
          error.message ||
            'Unable to complete payment. Please check card details and try again.'
        );
        return;
      }
      if (
        !paymentIntent ||
        (paymentIntent.status !== 'succeeded' &&
          paymentIntent.status !== 'requires_capture')
      ) {
        onError('Payment is still processing. Please wait and try again.');
        return;
      }
      const confirmed = await confirmPayment({
        order_id: checkoutSession.order_id,
        payment_intent_id:
          paymentIntent.id || checkoutSession.payment_intent_id || '',
      });
      onSuccess(confirmed);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Unable to finalize payment.';
      onError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handlePay}>
      <PaymentElement />
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || !elements || isSubmitting}
        >
          {isSubmitting ? 'Processing…' : submitLabel}
        </Button>
      </Stack>
    </Box>
  );
};

type StorefrontStripePaymentSectionProps = StorefrontStripePaymentFormProps;

/** Stripe Elements wrapper for chapter supplies card checkout (storefront or management). */
export const StorefrontStripePaymentSection: React.FC<
  StorefrontStripePaymentSectionProps
> = (props) => {
  const secret = props.checkoutSession.payment_client_secret?.trim();
  if (!secret || !stripePromise) {
    return null;
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: secret }}>
      <StorefrontStripePaymentFormInner {...props} />
    </Elements>
  );
};

export default StorefrontStripePaymentFormInner;
